import { useState } from "react";

import type { SessionUser } from "@/api/auth";
import { useGrantEditor, useReady, useSession, useSystemMetrics, useUsers } from "@/api/queries";
import { Button } from "@/components/Button";
import { MethodologyPanel } from "@/components/MethodologyPanel";
import { Panel } from "@/components/Panel";
import { RemoteState } from "@/components/RemoteState";
import { Select } from "@/components/Select";
import { StatusLabel } from "@/components/StatusLabel";
import { Tag } from "@/components/Tag";
import { roleLabel } from "@/lib/session";
import { useUiStore } from "@/store/ui";

interface MetricRow {
  label: string;
  value: string;
}

/**
 * 运行指标（specs/12）：HTTP + B6 业务指标快照，来自 `/system/metrics`。
 *
 * @dataSource remote
 */
export function SystemView() {
  const metrics = useSystemMetrics();
  const ready = useReady();
  const data = metrics.data;

  const httpRows: MetricRow[] = data
    ? [
        { label: "总请求数", value: String(data.http.requests_total) },
        { label: "错误数（4xx/5xx）", value: String(data.http.errors_total) },
        {
          label: "状态分布",
          value: `2xx=${data.http.by_status["2xx"]} · 3xx=${data.http.by_status["3xx"]} · 4xx=${data.http.by_status["4xx"]} · 5xx=${data.http.by_status["5xx"]}`,
        },
        {
          label: "延迟 p50 / p95 / max",
          value: `${data.http.durations_ms.p50}ms / ${data.http.durations_ms.p95}ms / ${data.http.durations_ms.max}ms`,
        },
      ]
    : [];

  const runs = data?.business.runs;
  const runsRows: MetricRow[] = runs
    ? [
        { label: "运行总数", value: String(runs.total) },
        {
          label: "按状态",
          value:
            Object.entries(runs.by_status)
              .map(([status, count]) => `${status}=${count}`)
              .join(" · ") || "—",
        },
      ]
    : [];

  const cost = data?.business.cost;
  const costRows: MetricRow[] = cost
    ? [
        { label: "记账流水", value: String(cost.ledger_entries) },
        { label: "输入 Token", value: cost.prompt_tokens.toLocaleString() },
        { label: "输出 Token", value: cost.completion_tokens.toLocaleString() },
        { label: "成本单位", value: cost.cost_units.toLocaleString() },
      ]
    : [];

  const artifacts = data?.business.artifacts;
  const artifactRows: MetricRow[] = artifacts
    ? [
        { label: "产物总数", value: String(artifacts.total) },
        { label: "已批准", value: String(artifacts.approved) },
        {
          label: "采纳率",
          value:
            artifacts.approval_rate === null
              ? "—（暂无产物）"
              : `${Math.round(artifacts.approval_rate * 100)}%`,
        },
      ]
    : [];

  const reviews = data?.business.reviews;
  const reviewRows: MetricRow[] = reviews
    ? [
        { label: "审阅记录", value: String(reviews.total) },
        {
          label: "按决策",
          value:
            Object.entries(reviews.by_decision)
              .map(([decision, count]) => `${decision}=${count}`)
              .join(" · ") || "—",
        },
      ]
    : [];

  return (
    <div className="page-scroll">
      <div className="page-width">
        {/*
          指标失败必须**呈现错误态**，而不是把四块面板渲染成空表——空表会被读成
          "这个项目什么都没有"，而底部的状态灯还会显示"近端无错误"（AC-1401 / RULES §11.2）。
          ⚠️ 模型配置已于 2026-10-07 迁往 `/studio/settings`（`specs/07` §4）：
          它按 `specs/13` 是**安装级**配置，与"这个项目的运行指标"不是同一类事实，
          挂在同一页会让入口名实不符而不可发现。
        */}
        <RemoteState queries={[metrics]} title="运行指标加载失败">
          <header className="page-heading">
            <div>
              <span className="eyebrow">APP_VERSION · {data?.api_version ?? "v1"}</span>
              <h2>系统运行指标与业务口径</h2>
              <p>
                成功率取自进程内实时观测；Token、成本、采纳率与审阅量按业务实时聚合，
                全部为当前运行的实测值，不是估算。
              </p>
            </div>
            <Button
              size="small"
              variant="quiet"
              id="refresh-system-metrics"
              disabled={metrics.isFetching}
              onClick={() => void metrics.refetch()}
            >
              {metrics.isFetching ? "刷新中…" : "刷新"}
            </Button>
          </header>

          {/* 就绪探针：区分「进程活着」与「依赖可用」（GET /health/ready） */}
          <div className="panel" style={{ marginBottom: 18 }}>
            <div className="meta-row" style={{ alignItems: "center", margin: 0 }}>
              <StatusLabel
                tone={ready.isError ? "danger" : ready.data?.ready ? "success" : "warning"}
              >
                {ready.isError ? "探针不可达" : ready.data?.ready ? "依赖就绪" : "依赖未就绪"}
              </StatusLabel>
              <Tag>数据库 {ready.data?.database ?? "—"}</Tag>
              <Tag>方法包 {ready.data?.methodology ?? "—"}</Tag>
              <span style={{ color: "var(--ink-soft)", fontSize: "10px" }}>
                {ready.isError
                  ? ready.error instanceof Error
                    ? ready.error.message
                    : "未知错误"
                  : (ready.data?.server_version ?? "")}
              </span>
            </div>
            {ready.data && !ready.data.ready && ready.data.error ? (
              <p className="inline-note" style={{ color: "var(--red)" }}>
                {ready.data.error}
              </p>
            ) : null}
          </div>

          <div className="growth-layout">
            <Panel
              title="HTTP 观测"
              description={`进程内指标 · 运行 ${data ? Math.round(data.uptime_seconds / 60) : "—"} 分钟`}
              bodyClassName="metric-rows"
            >
              {metrics.isPending ? (
                <p className="inline-note">加载中…</p>
              ) : (
                httpRows.map((row) => (
                  <div className="metric-row" key={row.label}>
                    <span className="metric-label">{row.label}</span>
                    <strong className="metric-value">{row.value}</strong>
                  </div>
                ))
              )}
            </Panel>

            <Panel title="生成任务" description="当前项目实时聚合" bodyClassName="metric-rows">
              {runsRows.map((row) => (
                <div className="metric-row" key={row.label}>
                  <span className="metric-label">{row.label}</span>
                  <strong className="metric-value">{row.value}</strong>
                </div>
              ))}
            </Panel>

            <Panel title="成本与额度" description="累计记账，非估算" bodyClassName="metric-rows">
              {costRows.map((row) => (
                <div className="metric-row" key={row.label}>
                  <span className="metric-label">{row.label}</span>
                  <strong className="metric-value">{row.value}</strong>
                </div>
              ))}
            </Panel>

            <Panel title="采纳与审阅" description="产物采纳率与审阅量" bodyClassName="metric-rows">
              <div style={{ marginBottom: 14 }}>
                {artifactRows.map((row) => (
                  <div className="metric-row" key={row.label}>
                    <span className="metric-label">{row.label}</span>
                    <strong className="metric-value">{row.value}</strong>
                  </div>
                ))}
              </div>
              {reviewRows.map((row) => (
                <div className="metric-row" key={row.label}>
                  <span className="metric-label">{row.label}</span>
                  <strong className="metric-value">{row.value}</strong>
                </div>
              ))}
            </Panel>
          </div>

          <section style={{ marginTop: 24 }}>
            <MethodologyPanel />
          </section>

          <AccountAdminSection />

          <section style={{ marginTop: 24 }}>
            <div className="section-title">
              <div>
                <h3>指标口径（B6）</h3>
                <p>业务指标随方法论版本与人工决定可追溯，见 specs/12 §2</p>
              </div>
              <Tag>失败可解释：任何异常都带 rule_id / field_path</Tag>
            </div>
            <div className="panel" style={{ padding: 0 }}>
              <div className="meta-row" style={{ alignItems: "center", margin: 0 }}>
                <StatusLabel tone={data && data.http.errors_total > 0 ? "warning" : "success"}>
                  {data?.http.errors_total ? "存在错误响应" : "近端无错误"}
                </StatusLabel>
                <span style={{ alignSelf: "center", color: "var(--ink-soft)", fontSize: "10px" }}>
                  观测端点自身不计入流量，避免反馈环（specs/12 §3）
                </span>
              </div>
            </div>
          </section>
        </RemoteState>
      </div>
    </div>
  );
}

const CREATION_ROLES: { value: string; label: string }[] = [
  { value: "head_writer", label: "总编剧" },
  { value: "writer", label: "编剧" },
  { value: "designer", label: "设计" },
  { value: "reviewer", label: "审阅" },
];

/**
 * 账号与创作权限（`specs/15` §2 · PR-1510）：开通编辑账号。
 *
 * 仅 `admin` **显示**入口——但**权限由后端判**（`GET /auth/users` 与 grant-editor 都仅 admin，
 * 非 admin 返回 `403`）：前端隐藏只是不提供无效入口，不是权限边界（CONSTITUTION A3）。
 */
function AccountAdminSection() {
  const session = useSession();
  const isAdmin =
    session.data?.platform_role === "editor" && session.data.creation_role === "admin";
  const users = useUsers(Boolean(isAdmin));

  if (!isAdmin) return null;

  return (
    <section style={{ marginTop: 24 }}>
      <div className="section-title">
        <div>
          <h3>账号与创作权限</h3>
          <p>玩家账号只能参与观众侧；开通为编辑后按所选创作角色进入创作台。</p>
        </div>
        <Tag>仅管理员可操作</Tag>
      </div>
      <div className="panel" style={{ padding: 0 }}>
        {users.isPending ? (
          <p className="inline-note" style={{ padding: 14 }}>
            加载中…
          </p>
        ) : null}
        {users.isError ? (
          <p className="inline-note" style={{ padding: 14, color: "var(--red)" }}>
            账号列表加载失败：{users.error instanceof Error ? users.error.message : "未知错误"}
          </p>
        ) : null}
        {(users.data ?? []).map((user) => (
          <UserRow key={user.id} user={user} />
        ))}
      </div>
    </section>
  );
}

function UserRow({ user }: { user: SessionUser }) {
  const grant = useGrantEditor();
  const pushToast = useUiStore((state) => state.pushToast);
  const [role, setRole] = useState("writer");

  return (
    <div
      className="meta-row"
      style={{ alignItems: "center", margin: 0, padding: "10px 14px", gap: 10 }}
    >
      <strong>{user.display_name || user.email}</strong>
      <span style={{ color: "var(--ink-soft)", fontSize: "10px" }}>{user.email}</span>
      <StatusLabel tone={user.platform_role === "editor" ? "success" : "warning"}>
        {roleLabel(user)}
      </StatusLabel>
      {user.platform_role === "player" ? (
        <span style={{ marginLeft: "auto", display: "inline-flex", gap: 8, alignItems: "center" }}>
          <Select
            id={`grant-role-${user.id}`}
            aria-label={`${user.email} 的创作角色`}
            value={role}
            options={CREATION_ROLES}
            onChange={(event) => setRole(event.target.value)}
          />
          <Button
            size="small"
            variant="quiet"
            disabled={grant.isPending}
            onClick={() =>
              grant.mutate(
                { userId: user.id, creationRole: role },
                {
                  onSuccess: (updated) =>
                    pushToast({
                      title: "已开通编辑",
                      detail: `${updated.email} · ${roleLabel(updated)}`,
                      mark: "✓",
                    }),
                  onError: (error) =>
                    pushToast({
                      title: "开通失败",
                      detail: error instanceof Error ? error.message : "未知错误",
                      mark: "!",
                    }),
                },
              )
            }
          >
            开通为编辑
          </Button>
        </span>
      ) : null}
    </div>
  );
}
